import PitchGrid from './PitchGrid.jsx';
import StatTiles from './StatTiles.jsx';
import SeasonHistory from './SeasonHistory.jsx';
import LineChart from '@/components/charts/LineChart.jsx';
import ScatterChart from '@/components/charts/ScatterChart.jsx';

const nf = new Intl.NumberFormat('en-GB');

/*
 * The squad, stats and charts, shared by the live manager page and the
 * archived-season page. The two endpoints differ enough that each page owns
 * its own data loading and passes the normalised pieces in here.
 */
export default function ManagerView({
	data,
	picks,
	chips,
	title,
	subtitle,
	navigating,
	showPoints,
	onPrev,
	onNext,
	canGoPrev,
	canGoNext,
}) {
	if (!data) return null;

	return (
		<>
			<section className="squad">
				<div className="squad__header">
					<button
						className="btn btn--secondary btn--icon"
						type="button"
						onClick={onPrev}
						disabled={!canGoPrev || navigating}
						aria-label="Previous gameweek"
					>
						←
					</button>

					<div className="squad__title">
						<h2>{title}</h2>
						<p className="squad__meta">{subtitle}</p>
					</div>

					<button
						className="btn btn--secondary btn--icon"
						type="button"
						onClick={onNext}
						disabled={!canGoNext || navigating}
						aria-label="Next gameweek"
					>
						→
					</button>
				</div>

				<div className="squad__summary">
					{data.gw_or != null && (
						<div className="squad__stat">
							<span className="squad__stat-value numeric">
								{nf.format(data.gw_or)}
							</span>
							<span className="squad__stat-label">Overall rank</span>
						</div>
					)}
					{showPoints && (
						<div className="squad__stat squad__stat--accent">
							<span className="squad__stat-value numeric">
								{nf.format(data.points + data.cost)}
								{data.cost ? (
									<small className="squad__stat-sub">
										{' '}
										({nf.format(data.cost * -1)})
									</small>
								) : null}
							</span>
							<span className="squad__stat-label">Points</span>
						</div>
					)}
				</div>

				<PitchGrid
					playingPicks={picks.playing}
					benchPicks={picks.bench}
					captain={data.captain}
					viceCaptain={data.vicecaptain}
					loading={navigating}
				/>
			</section>

			<section className="manager-page__stats">
				<h2 className="section-title">Season stats</h2>
				<StatTiles data={data} chips={chips} />
			</section>

			<SeasonHistory seasons={data.season_history} />

			<section className="manager-page__charts">
				<h2 className="section-title">Gameweek history</h2>
				<p className="manager-page__hint">
					Tap a legend entry to hide or show a line.
				</p>

				<div className="chart-grid">
					<LineChart
						title="Overall rank"
						xLabels={data.gw_event}
						seriesLabels={data.comp_manager_label}
						series={data.comp_overall_rank}
						logRankScale
					/>
					<LineChart
						title="Gameweek rank"
						xLabels={data.gw_event}
						seriesLabels={data.comp_manager_label}
						series={data.comp_gw_rank}
					/>
					<LineChart
						title="Gameweek points"
						xLabels={data.gw_event}
						seriesLabels={data.comp_manager_label}
						series={data.comp_gw_points}
					/>
					{/* The archive payload only carries captain data for seasons
					    where it was recorded. */}
					{data.comp_captain_scores?.length > 0 && (
						<LineChart
							title="Captain points"
							xLabels={data.gw_event}
							seriesLabels={data.comp_manager_label}
							series={data.comp_captain_scores}
							pointLabels={data.comp_captain_names}
						/>
					)}
					{data.itg_score?.length > 0 && (
						<LineChart
							title="Immediate transfer gain (ITG)"
							xLabels={data.itg_event}
							seriesLabels={[data.comp_manager_label?.[0]]}
							series={data.itg_score}
						/>
					)}
					<LineChart
						title="Team value"
						xLabels={data.gw_event}
						seriesLabels={data.comp_manager_label}
						series={data.comp_gw_tv}
					/>
					{data.transfers_time_percentile?.length > 0 && (
						<ScatterChart
							title="When transfers were made"
							label="Percentile between deadlines"
							values={data.transfers_time_percentile}
						/>
					)}
				</div>
			</section>
		</>
	);
}
